<template>
  <ClientOnly>
    <CardSlider :cards="cardsToDisplay" :title="configData?.Header">
      <template #default="slotProps">
        <ProductCard
          :card-data="slotProps.card"
          :card-index="slotProps.index"
        />
      </template>
    </CardSlider>

    <template #fallback>
      <div
        class="w-full h-500 motion-safe:animate-pulse motion-reduce:animate-none"
        aria-hidden="true"
      >
        <div class="my-16 w-full flex justify-between">
          <span class="flex self-center w-200 h-28 pb-2 bg-text-disabled" />
          <div class="flex">
            <span class="w-44 h-44 rounded-md bg-text-disabled me-8" />
            <span class="w-44 h-44 rounded-md bg-text-disabled" />
          </div>
        </div>
        <div class="grid gap-16 grid-cols-3 md:grid-cols-5 grid-rows-1">
          <div class="w-full h-[420px] rounded-md bg-text-disabled" />
          <div class="w-full h-[420px] rounded-md bg-text-disabled" />
          <div class="w-full h-[420px] rounded-md bg-text-disabled" />
          <div
            class="w-full h-[420px] rounded-md bg-text-disabled hidden md:block"
          />
          <div
            class="w-full h-[420px] rounded-md bg-text-disabled hidden md:block"
          />
        </div>
      </div>
    </template>
  </ClientOnly>
</template>

<script lang="ts" setup>
// This component is client only because it needs javascript
// to determine the width of the cards depending on the client width,
// and that is prone to cause a hydration error.
// Therefore we have it ClientOnly and use loading UI to minimize
// layout shifts

import type { ListingProduct } from '~/models/ListingProduct.api.model';
import { filter_productCardsByPlanVariant } from '~/utils/general/filter_productCardsByPlanVariant';
import {
  DEFAULT_PLAN_ID,
  DEFAULT_MONTHLY_PAYMENT_PERIOD,
} from '~/utils/pdp/queryParamHelpers';

interface Props {
  configData: {
    Data?: ListingProduct[];
    Header?: string;
    DefaultPlanId?: number;
    SliderVariantsAmountMonthlyPayments?: number;
  };
}

const props = defineProps<Props>();

const planId = computed(
  () => props.configData?.DefaultPlanId ?? Number(DEFAULT_PLAN_ID),
);
const amountMonthlyPayments = computed(
  () =>
    props.configData?.SliderVariantsAmountMonthlyPayments ??
    Number(DEFAULT_MONTHLY_PAYMENT_PERIOD),
);
const bindingPeriodMonths = computed(() =>
  determineSelectedBindingPeriod(amountMonthlyPayments.value),
);

const cardsToDisplay = computed(() => {
  return filter_productCardsByPlanVariant(
    props.configData?.Data ?? [],
    planId.value,
    amountMonthlyPayments.value,
    bindingPeriodMonths.value,
  );
});
</script>
